/**
 * Resource Plan Distribution Contract
 * ───────────────────────────────────
 * Self-describing, versioned configuration for how a resource plan is
 * distributed across phases and roles. Stored as the JSONB `config` of a
 * `sales.deal_engine_profile` row where `profile_type = 'resource_plan'`.
 *
 * Discriminated on `distribution_model`:
 *   'size_tiered'     - classify project size from total hours → size-specific
 *                       velocity + role allocations; phase hours from a
 *                       section→phase map. (The legacy algorithm.)
 *   'flat_percentage' - size-independent: phase hours = grandTotal × phase %,
 *                       role hours = grandTotal × role %, single flat velocity,
 *                       arbitrary/custom phase names.
 *
 * The `distribution_model` value is mirrored into the `profile_subtype` column
 * for query/filtering; the JSON is the source of truth.
 *
 * Validation is deliberately STRUCTURAL only (phase-key integrity, types). It
 * does NOT require weights/percentages to sum to 1 - the engine renormalises
 * via floor-then-reconcile, exactly as the original algorithm did.
 */
import { z } from 'zod';
export const RESOURCE_PLAN_CONTRACT_VERSION = '1.0.0';
// ── Shared phase declaration ──────────────────────────────────────────────────
/**
 * An ordered, named phase carrying its own display metadata. `key` is the
 * stable identifier referenced across the contract (phase_weights /
 * phase_section_map / phase_percentages) and emitted into the output; `label`
 * is the human-facing display name; `order` fixes timeline ordering.
 */
export const phaseDefinitionSchema = z.object({
    key: z.string().min(1),
    label: z.string().min(1),
    order: z.number().int().nonnegative(),
    /**
     * Phases sharing a `display_group` are merged into a single timeline bar by
     * the frontend (size_tiered groups discovery+design). Omit ⇒ own segment.
     */
    display_group: z.string().optional(),
    display_group_label: z.string().optional(),
    /**
     * End-of-phase milestone marker for the Gantt (size_tiered marks
     * "Design Sign-off" on design and "Go-Live" on test). Omit ⇒ no marker.
     */
    milestone_label: z.string().optional(),
    /**
     * Phase keys this phase starts after. The engine schedules
     * start_week = max over deps of (dep.start_week + dep.weeks); phases sharing
     * (or lacking) prerequisites overlap.
     *   ABSENT ⇒ waterfall (starts right after the previous phase by `order`).
     *   []     ⇒ starts at week 1 (an explicitly parallel root).
     * Each referenced key must exist and have a strictly-lower `order` (enforced
     * on the union) so the graph is a cycle-free DAG resolvable in one pass.
     */
    depends_on: z.array(z.string()).optional(),
});
/**
 * Per-plan visibility toggles for the timeline bar and Gantt chart. Mirrored
 * into `ResourcingOutput.display` and read by the pane and the exports. Each
 * flag defaults to `true` (shown) when absent.
 */
export const resourcePlanDisplaySchema = z
    .object({
    show_timeline: z.boolean().optional(),
    show_gantt: z.boolean().optional(),
})
    .optional();
const phaseListSchema = z
    .array(phaseDefinitionSchema)
    .min(1)
    .superRefine((phases, ctx) => {
    const seen = new Set();
    for (const p of phases) {
        if (seen.has(p.key)) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, message: `duplicate phase key: ${p.key}` });
        }
        seen.add(p.key);
    }
});
/** role_id → (phase key → fractional weight). */
const phaseWeightsSchema = z.record(z.string(), z.record(z.string(), z.number()));
// ── size_tiered: section → phase mapping ──────────────────────────────────────
/**
 * Declares where a single phase's hour pool comes from. Replaces the previously
 * hardcoded `derivePhaseHours` logic. A phase pulls from any combination of
 * whole sections and specific subsections.
 */
export const phaseSectionSourceSchema = z.object({
    /** Phase key this source feeds (must exist in `phases`). */
    phase_key: z.string().min(1),
    /** Whole section keys whose hours are summed into this phase. */
    section_keys: z.array(z.string()).default([]),
    /** Specific subsection pulls. */
    subsections: z
        .array(z.object({
        section_key: z.string().min(1),
        subsection_key: z.string().min(1),
        /**
         * When the subsection is absent, fall back to this fraction of the
         * parent section's hours (computed as `floor(parent.hours × fraction)`).
         * The legacy `design` phase used 0.5. Omit ⇒ contributes 0 when absent.
         */
        split_fallback: z.number().min(0).max(1).optional(),
    }))
        .default([]),
});
const projectSizeThresholdSchema = z.object({
    size: z.string().min(1),
    max_hours: z.number().nullable(),
});
export const sizeTieredContractSchema = z.object({
    distribution_model: z.literal('size_tiered'),
    schemaVersion: z.literal(RESOURCE_PLAN_CONTRACT_VERSION),
    /** User-friendly display name for this distribution model (shown in admin UI). */
    name: z.string().optional(),
    /** Optional helper text shown under the velocity field in the admin UI. */
    description: z.string().optional(),
    phases: phaseListSchema,
    /** Visibility toggles for the timeline / Gantt (default both shown). */
    display: resourcePlanDisplaySchema,
    /** Ascending by max_hours; null max_hours = unbounded top tier. */
    project_size_thresholds: z.array(projectSizeThresholdSchema).min(1),
    /** size key → team velocity (hours/week). */
    team_velocity: z.record(z.string(), z.number().positive()),
    /** size key → (role_id → fractional allocation of grand total). */
    role_allocations: z.record(z.string(), z.record(z.string(), z.number())),
    /** role_id → (phase key → fractional weight). */
    phase_weights: phaseWeightsSchema,
    /** Per-phase hour-pool sources. Replaces the hardcoded section→phase mapping. */
    phase_section_map: z.array(phaseSectionSourceSchema).min(1),
    /**
     * Section → role direct-assignment overrides. Each listed section's full hours
     * are assigned DIRECTLY to `role_id` BEFORE the `role_allocations` percentage
     * split runs on the remaining hours; override roles are then excluded from the
     * percentage pool and the surviving weights are renormalized to 1. Default []
     * ⇒ legacy pure-percentage behavior (so other contracts are unaffected).
     */
    role_section_overrides: z
        .array(z.object({
        role_id: z.string().min(1),
        // May be empty (a half-authored entry in the admin editor); an override
        // with no sections simply carves out 0 hours, so it's a harmless no-op.
        section_keys: z.array(z.string().min(1)).default([]),
    }))
        .default([]),
});
// ── flat_percentage ───────────────────────────────────────────────────────────
export const flatPercentageContractSchema = z.object({
    distribution_model: z.literal('flat_percentage'),
    schemaVersion: z.literal(RESOURCE_PLAN_CONTRACT_VERSION),
    /** User-friendly display name for this distribution model (shown in admin UI). */
    name: z.string().optional(),
    /** Optional helper text shown under the velocity field in the admin UI. */
    description: z.string().optional(),
    phases: phaseListSchema,
    /** Visibility toggles for the timeline / Gantt (default both shown). */
    display: resourcePlanDisplaySchema,
    /** Single flat velocity (hours/week): timeline_weeks = ceil(total / velocity). */
    flat_velocity: z.number().positive(),
    /** phase key → fraction of grand total hours assigned to that phase. */
    phase_percentages: z.record(z.string(), z.number()),
    /** role_id → fraction of grand total hours assigned to that role (size-independent). */
    role_percentages: z.record(z.string(), z.number()),
    /** role_id → (phase key → fractional weight) for per-role phase distribution. */
    phase_weights: phaseWeightsSchema,
});
// ── ai: per-deal AI distribution ────────────────────────────────────────────────
/**
 * AI-driven distribution: the engine carries NO static role/phase machinery
 * (no phases, flat_velocity, phase_percentages, role_percentages, phase_weights).
 * The total hours are distributed across the deal CATEGORY's roster per deal by
 * the AI, with the Project Manager fixed at the calculator's `pm_percent`. The
 * candidate roster is the set of roles with an exact per-category
 * `resource_role_description` (resolved at estimate time), not declared here.
 * There is no phase/timeline concept, so the output sets show_timeline/show_gantt
 * off.
 */
export const aiContractSchema = z.object({
    distribution_model: z.literal('ai'),
    schemaVersion: z.literal(RESOURCE_PLAN_CONTRACT_VERSION),
    /** User-friendly display name for this distribution model (shown in admin UI). */
    name: z.string().optional(),
    /** Optional helper text shown in the admin UI. */
    description: z.string().optional(),
    /**
     * Role_ids the AI should weight higher when distributing (a subset of the
     * category roster; the Project Manager is excluded - it is fixed at
     * `pm_percent`). Surfaced in the inference prompt as priority guidance. Empty
     * or absent ⇒ no priority bias.
     */
    priority_roles: z.array(z.string()).optional(),
    /** Visibility toggles for the timeline / Gantt (ai mode defaults both off). */
    display: resourcePlanDisplaySchema,
});
// ── Discriminated union + cross-field validation ──────────────────────────────
/** Adds an issue for any phase key referenced by the contract that isn't declared. */
function refinePhaseKeyIntegrity(contract, ctx) {
    const declared = new Set(contract.phases.map((p) => p.key));
    const flag = (key, where) => {
        if (!declared.has(key)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `${where} references undeclared phase key: ${key}`,
            });
        }
    };
    for (const [roleId, weights] of Object.entries(contract.phase_weights)) {
        for (const key of Object.keys(weights))
            flag(key, `phase_weights[${roleId}]`);
    }
    if (contract.distribution_model === 'size_tiered') {
        for (const src of contract.phase_section_map)
            flag(src.phase_key, 'phase_section_map');
    }
    else {
        for (const key of Object.keys(contract.phase_percentages))
            flag(key, 'phase_percentages');
    }
}
/**
 * Validates phase dependencies (`depends_on`): every referenced key must exist,
 * a phase may not depend on itself, and a dependency must have a strictly-lower
 * `order`. The strictly-lower-order rule makes the dependency graph a cycle-free
 * DAG resolvable by the engine in a single ascending-order pass.
 */
function refinePhaseDependencyIntegrity(contract, ctx) {
    const byKey = new Map(contract.phases.map((p) => [p.key, p]));
    for (const p of contract.phases) {
        if (!p.depends_on)
            continue;
        for (const dep of p.depends_on) {
            if (dep === p.key) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: `phase "${p.key}" cannot depend on itself`,
                });
                continue;
            }
            const target = byKey.get(dep);
            if (!target) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: `phase "${p.key}" depends_on undeclared phase key: ${dep}`,
                });
                continue;
            }
            if (target.order >= p.order) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: `phase "${p.key}" (order ${p.order}) depends_on "${dep}" (order ${target.order}); dependencies must reference a strictly-lower order`,
                });
            }
        }
    }
}
/**
 * Validates `role_section_overrides` (size_tiered only): a single section key
 * may be direct-assigned to at most one role, otherwise its hours would be
 * double-counted across roles.
 */
function refineSectionOverrideIntegrity(contract, ctx) {
    if (contract.distribution_model !== 'size_tiered')
        return;
    const seen = new Set();
    for (const ov of contract.role_section_overrides) {
        for (const key of ov.section_keys) {
            if (seen.has(key)) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: `role_section_overrides assigns section "${key}" to more than one role`,
                });
            }
            seen.add(key);
        }
    }
}
export const resourcePlanContractSchema = z
    .discriminatedUnion('distribution_model', [
    sizeTieredContractSchema,
    flatPercentageContractSchema,
    aiContractSchema,
])
    .superRefine((contract, ctx) => {
    // The phase-integrity refinements dereference phases/phase_weights, which the
    // `ai` model does not carry - it has no phase/timeline machinery at all.
    if (contract.distribution_model === 'ai')
        return;
    refinePhaseKeyIntegrity(contract, ctx);
    refinePhaseDependencyIntegrity(contract, ctx);
    refineSectionOverrideIntegrity(contract, ctx);
});
/**
 * Default user-friendly labels for each distribution model. Used as the fallback
 * display name (when a contract has no `name`), the admin type-dropdown
 * labels, and the seeded `displayName`. The contract's `name` overrides it.
 */
export const DISTRIBUTION_MODEL_LABELS = {
    size_tiered: 'Size-Tiered',
    flat_percentage: 'Flat Percentage',
    ai: 'AI-Driven',
};
// ── Legacy normalization ──────────────────────────────────────────────────────
/**
 * Canonical `size_tiered` phase definitions. Used by the legacy normalizer (so
 * un-reseeded profiles render exactly as before) and as the reference shape for
 * the seeded size_tiered templates. Labels/groups/milestones mirror what the
 * frontend Gantt hardcoded prior to this change.
 */
export const CANONICAL_SIZE_TIERED_PHASES = [
    {
        key: 'discovery',
        label: 'Discovery',
        order: 0,
        display_group: 'discovery_design',
        display_group_label: 'Discovery & Design',
    },
    {
        key: 'design',
        label: 'Design',
        order: 1,
        display_group: 'discovery_design',
        display_group_label: 'Discovery & Design',
        milestone_label: 'Design Sign-off',
    },
    { key: 'build', label: 'NetSuite Build', order: 2 },
    { key: 'test', label: 'UAT & Testing', order: 3, milestone_label: 'Go-Live' },
    { key: 'deploy', label: 'Go-Live', order: 4 },
    { key: 'hypercare', label: 'Post Go-Live Support', order: 5 },
];
/** The hardcoded section→phase map that the legacy `derivePhaseHours` applied. */
export function legacyPhaseSectionMap(buildSectionKeys) {
    return [
        {
            phase_key: 'discovery',
            section_keys: [],
            subsections: [{ section_key: 'discovery_design', subsection_key: 'discovery' }],
        },
        {
            phase_key: 'design',
            section_keys: [],
            subsections: [
                { section_key: 'discovery_design', subsection_key: 'design', split_fallback: 0.5 },
            ],
        },
        { phase_key: 'build', section_keys: buildSectionKeys, subsections: [] },
        { phase_key: 'test', section_keys: ['testing'], subsections: [] },
        { phase_key: 'deploy', section_keys: ['go_live'], subsections: [] },
        { phase_key: 'hypercare', section_keys: ['post_go_live'], subsections: [] },
    ];
}
// ── Role-allocation inference (AI) ────────────────────────────────────────────
/**
 * Tolerance for the "fractions sum to 1" check on inferred allocations. The engine
 * re-normalizes anyway (floor-then-reconcile / normalizeWeights), so this only needs to
 * reject grossly-wrong output and let generateValidated re-prompt; small model rounding
 * is fine.
 */
export const ROLE_ALLOCATION_SUM_TOLERANCE = 0.02;
/** Confidence band the inference reports, mirrored from the estimate-inference output shape. */
export const roleAllocationConfidenceSchema = z.enum(['high', 'medium', 'low']);
/**
 * Build the Zod validator for an AI role-allocation inference, parameterized by the deal's
 * distribution model + the active rate-card role_ids. Enforces: keys ⊆ validRoleIds, each
 * fraction in [0,1], non-empty, and each scope summing to ~1. Used as `generateValidated`'s
 * validator so the model self-corrects on the formatted errors (e.g. "sum is 0.93").
 *
 * Output is forward-compatible with the past-deal/hybrid phase: it carries confidence +
 * evidence + reasoning alongside the allocations.
 */
export function buildRoleAllocationInferenceSchema(shape) {
    const validRoles = new Set(shape.validRoleIds);
    const weightMapSchema = (where) => z
        .record(z.string().min(1), z.number().min(0).max(1))
        .superRefine((map, ctx) => {
        const keys = Object.keys(map);
        if (keys.length === 0) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, message: `${where} must not be empty` });
            return;
        }
        for (const key of keys) {
            if (!validRoles.has(key)) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: `${where}: unknown role_id "${key}". Valid roles: ${shape.validRoleIds.join(', ')}`,
                });
            }
        }
        const sum = keys.reduce((s, k) => s + (map[k] ?? 0), 0);
        if (Math.abs(sum - 1) > ROLE_ALLOCATION_SUM_TOLERANCE) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `${where} fractions must sum to 1 (got ${sum.toFixed(3)})`,
            });
        }
    });
    const meta = {
        confidence: roleAllocationConfidenceSchema,
        evidence: z.array(z.string()).default([]),
        reasoning: z.string().default(''),
    };
    if (shape.distributionModel === 'flat_percentage') {
        return z.object({
            distribution_model: z.literal('flat_percentage'),
            role_percentages: weightMapSchema('role_percentages'),
            ...meta,
        });
    }
    const sizeKeys = shape.sizeKeys ?? [];
    return z.object({
        distribution_model: z.literal('size_tiered'),
        role_allocations: z
            .record(z.string().min(1), weightMapSchema('role_allocations[size]'))
            .superRefine((rec, ctx) => {
            for (const sk of sizeKeys) {
                if (!(sk in rec)) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: `role_allocations is missing required size "${sk}"`,
                    });
                }
            }
        }),
        ...meta,
    });
}
/**
 * Upgrade a raw resource-plan config to the contract shape (non-mutating).
 *
 * - A config that already declares `distribution_model` passes through (a stray
 *   legacy `profile_subtype` field is stripped). Idempotent.
 * - A legacy config (no `distribution_model`, `phases: string[]`,
 *   `build_section_keys: string[]`) is inferred as `size_tiered`: phase
 *   definitions and a `phase_section_map` are synthesized so the resulting
 *   contract reproduces the original algorithm's behavior exactly.
 *
 * Lives in `@protoss/shared` so the API loader and DB seeds share one copy.
 */
export function normalizeResourcePlanContract(raw) {
    if (raw == null || typeof raw !== 'object')
        return raw;
    const cfg = raw;
    // Already a contract → pass through, dropping any stray legacy field.
    // Tolerate pre-rename rows (restored backups, old exports): drop the old
    // `schema_version` key and coerce any non-x.x.x value to the current
    // contract version - the contract has only ever had one shape.
    if (typeof cfg.distribution_model === 'string') {
        const { profile_subtype: _ps, schema_version: _legacy, ...rest } = cfg;
        if (typeof rest.schemaVersion !== 'string' || !/^\d+\.\d+\.\d+$/.test(rest.schemaVersion)) {
            rest.schemaVersion = RESOURCE_PLAN_CONTRACT_VERSION;
        }
        return rest;
    }
    // Legacy → infer size_tiered.
    const rawPhases = Array.isArray(cfg.phases) ? cfg.phases : [];
    const phasesAreObjects = rawPhases.length > 0 && typeof rawPhases[0] === 'object';
    const labelByKey = new Map(CANONICAL_SIZE_TIERED_PHASES.map((p) => [p.key, p]));
    const phases = phasesAreObjects
        ? rawPhases
        : rawPhases.map((key, i) => labelByKey.get(key) ?? {
            key,
            label: key.charAt(0).toUpperCase() + key.slice(1),
            order: i,
        });
    const buildSectionKeys = Array.isArray(cfg.build_section_keys)
        ? cfg.build_section_keys
        : [];
    const { profile_subtype: _ps, build_section_keys: _bsk, ...rest } = cfg;
    return {
        ...rest,
        distribution_model: 'size_tiered',
        schemaVersion: RESOURCE_PLAN_CONTRACT_VERSION,
        phases: phases.length > 0 ? phases : CANONICAL_SIZE_TIERED_PHASES,
        phase_section_map: cfg.phase_section_map ?? legacyPhaseSectionMap(buildSectionKeys),
    };
}
//# sourceMappingURL=resource-plan-contract.js.map